<script setup>
import OverviewReportFilters from './OverviewReportFilters.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { formatTime } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Table from 'dashboard/components/table/Table.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { computed, onMounted, ref, watch, h } from 'vue';
import BarChart from 'shared/components/charts/BarChart.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'account',
  },
  getterKey: {
    type: String,
    default: '',
  },
  actionKey: {
    type: String,
    default: '',
  },
  summaryKey: {
    type: String,
    default: '',
  },
  fetchItemsKey: {
    type: String,
    required: true,
  },
  // Quando since/until são passados (uso no Dashboard), o filtro próprio do
  // componente (OverviewReportFilters) some e o período vem de fora.
  since: {
    type: Date,
    default: null,
  },
  until: {
    type: Date,
    default: null,
  },
  businessHours: {
    type: Boolean,
    default: false,
  },
  // Rota do link "ver detalhe" de cada linha; sem isso, cai no padrão de
  // Relatórios (`${type}_reports_show`) — ver SummaryReportLink.vue.
  detailRouteName: {
    type: String,
    default: '',
  },
  // Barra horizontal ranqueada por conversas resolvidas, ao lado da tabela
  // (só no Dashboard — ver SummarySection.vue; Relatórios não passa isso).
  showRankingChart: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();

const isExternallyDriven = computed(() => !!(props.since && props.until));

const internalFrom = ref(0);
const internalTo = ref(0);
const internalBusinessHours = ref(false);

const from = computed(() =>
  isExternallyDriven.value
    ? Math.floor(props.since.getTime() / 1000)
    : internalFrom.value
);
const to = computed(() =>
  isExternallyDriven.value
    ? Math.floor(props.until.getTime() / 1000)
    : internalTo.value
);
const effectiveBusinessHours = computed(() =>
  isExternallyDriven.value ? props.businessHours : internalBusinessHours.value
);
import { useI18n } from 'vue-i18n';
import SummaryReportLink from './SummaryReportLink.vue';

const flagMap = {
  agent: 'isFetchingAgentSummaryReports',
  inbox: 'isFetchingInboxSummaryReports',
  team: 'isFetchingTeamSummaryReports',
  label: 'isFetchingLabelSummaryReports',
};

const uiFlags = useMapGetter('summaryReports/getUIFlags');
const isLoading = computed(() => uiFlags.value[flagMap[props.type]] ?? false);

const rowItems = useMapGetter([props.getterKey]) || [];
const reportMetrics = useMapGetter([props.summaryKey]) || [];

const getMetrics = id =>
  reportMetrics.value.find(metrics => metrics.id === Number(id)) || {};
const columnHelper = createColumnHelper();
const { t } = useI18n();

const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? '' : 'text-n-slate-12',
    },
    cellProps.getValue()
  );

const columns = computed(() => [
  columnHelper.accessor('name', {
    header: t(`SUMMARY_REPORTS.${props.type.toUpperCase()}`),
    width: 300,
    cell: cellProps =>
      h(SummaryReportLink, { ...cellProps, routeName: props.detailRouteName }),
  }),
  columnHelper.accessor('conversationsCount', {
    header: t('SUMMARY_REPORTS.CONVERSATIONS'),
    width: 200,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('avgFirstResponseTime', {
    header: t('SUMMARY_REPORTS.AVG_FIRST_RESPONSE_TIME'),
    width: 200,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('avgResolutionTime', {
    header: t('SUMMARY_REPORTS.AVG_RESOLUTION_TIME'),
    width: 200,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('avgReplyTime', {
    header: t('SUMMARY_REPORTS.AVG_REPLY_TIME'),
    width: 200,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('resolutionsCount', {
    header: t('SUMMARY_REPORTS.RESOLUTION_COUNT'),
    width: 200,
    cell: defaulSpanRender,
  }),
]);

const renderAvgTime = value => (value ? formatTime(value) : '--');

const renderCount = value => (value ? value.toLocaleString() : '--');

const tableData = computed(() =>
  rowItems.value.map(row => {
    const rowMetrics = getMetrics(row.id);
    const {
      conversationsCount,
      avgFirstResponseTime,
      avgResolutionTime,
      avgReplyTime,
      resolvedConversationsCount,
    } = rowMetrics;
    return {
      id: row.id,
      // we fallback on title, label for instance does not have a name property
      name: row.name ?? row.title,
      type: props.type,
      conversationsCount: renderCount(conversationsCount),
      avgFirstResponseTime: renderAvgTime(avgFirstResponseTime),
      avgReplyTime: renderAvgTime(avgReplyTime),
      avgResolutionTime: renderAvgTime(avgResolutionTime),
      resolutionsCount: renderCount(resolvedConversationsCount),
    };
  })
);

const rankedByResolutions = computed(() =>
  rowItems.value
    .map(row => ({
      name: row.name ?? row.title,
      resolutionsCount: getMetrics(row.id).resolvedConversationsCount || 0,
    }))
    .sort((a, b) => b.resolutionsCount - a.resolutionsCount)
);

const chartCollection = computed(() => ({
  labels: rankedByResolutions.value.map(row => row.name),
  datasets: [
    {
      label: t('SUMMARY_REPORTS.RESOLUTION_COUNT'),
      backgroundColor: 'rgb(31, 147, 255)',
      data: rankedByResolutions.value.map(row => row.resolutionsCount),
    },
  ],
}));

const chartOptions = {
  indexAxis: 'y',
  plugins: { legend: { display: false } },
  scales: {
    x: { beginAtZero: true, ticks: { precision: 0 } },
    y: { grid: { drawOnChartArea: false } },
  },
};

const chartHeight = computed(
  () => `${rankedByResolutions.value.length * 36 + 24}px`
);

const fetchReportsWithRetry = async () => {
  const params = {
    since: from.value,
    until: to.value,
    businessHours: effectiveBusinessHours.value,
  };
  try {
    await store.dispatch(props.actionKey, params);
  } catch {
    try {
      await store.dispatch(props.actionKey, params);
    } catch {
      useAlert(t('REPORT.SUMMARY_FETCHING_FAILED'));
    }
  }
};

const fetchAllData = () => {
  store.dispatch(props.fetchItemsKey);
  fetchReportsWithRetry();
};

onMounted(() => fetchAllData());

watch(
  () => [props.since, props.until, props.businessHours],
  () => {
    if (isExternallyDriven.value) fetchAllData();
  }
);

const onFilterChange = updatedFilter => {
  internalFrom.value = updatedFilter.from;
  internalTo.value = updatedFilter.to;
  internalBusinessHours.value = updatedFilter.businessHours;
  fetchAllData();
};

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

// downloadReports method is not used in this component
// but it is exposed to be used in the parent component
const downloadReports = () => {
  const dispatchMethods = {
    agent: 'downloadAgentReports',
    label: 'downloadLabelReports',
    inbox: 'downloadInboxReports',
    team: 'downloadTeamReports',
  };
  if (dispatchMethods[props.type]) {
    const fileName = generateFileName({
      type: props.type,
      to: to.value,
      businessHours: effectiveBusinessHours.value,
    });
    const params = {
      from: from.value,
      to: to.value,
      fileName,
      businessHours: effectiveBusinessHours.value,
    };
    store.dispatch(dispatchMethods[props.type], params);
  }
};

defineExpose({ downloadReports });
</script>

<template>
  <OverviewReportFilters
    v-if="!isExternallyDriven"
    :disabled="isLoading"
    @filter-change="onFilterChange"
  />
  <div class="flex flex-col gap-4 mt-5 md:flex-row md:items-start">
    <div
      class="relative flex-1 overflow-auto px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      :class="{ 'max-h-96': isExternallyDriven }"
    >
      <Table :table="table" />
      <Transition
        enter-active-class="transition-opacity duration-300 ease-out"
        leave-active-class="transition-opacity duration-200 ease-in"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isLoading"
          class="absolute inset-0 flex justify-center pt-[12.5rem] bg-n-solid-1/70 rounded-xl pointer-events-none"
        >
          <Spinner :size="32" class="text-n-brand" />
        </div>
      </Transition>
    </div>
    <div
      v-if="showRankingChart && !isLoading && rankedByResolutions.length"
      class="hidden p-4 shrink-0 md:block md:w-64 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      :style="{ height: chartHeight }"
    >
      <BarChart :collection="chartCollection" :chart-options="chartOptions" />
    </div>
  </div>
</template>
